import {analyticsLogger} from 'analytics/analyticsLogger';

import {DashboardColumns} from './dashboardLayout';
import {AgendaCalendar, TileId} from './dashboardModel';
import {TileContentStatus} from './tileCatalog';

export type DashboardInteractionAction =
  | 'tile_click'
  | 'row_click'
  | 'tab_click'
  | 'header_link_click'
  | 'facepile_click'
  | 'customize_open'
  | 'customize_close'
  | 'welcome_tooltip_show'
  | 'welcome_tooltip_dismiss'
  | 'layout_reset'
  | 'tile_add'
  | 'tile_remove'
  | 'tile_resize'
  | 'tile_reorder'
  | 'columns_change'
  | 'week_start_change'
  | 'calendar_view_change'
  | 'calendar_people_filter_change'
  | 'calendar_menu_open'
  | 'calendar_month_nav'
  | 'earnings_quarter_change'
  | 'earnings_page_nav'
  | 'calendar_day_tooltip_show'
  | 'search_open'
  | 'recent_search_chip'
  | 'recent_search_menu_open'
  | 'link_click'
  | 'agenda_expand'
  | 'agenda_collapse'
  | 'agenda_links_menu_open'
  | 'meeting_join'
  | 'description_toggle';

export type DashboardInteractionProps = {
  action: DashboardInteractionAction;
  tileId?: TileId;
  target?: string;
  value?: string;
  eventId?: string;
  destination?: string;
};

/** Window size and the column count the grid actually shows at that size. */
export type HomeViewportProps = {
  viewportWidth: number;
  viewportHeight: number;
  /** Columns on screen after the responsive caps (1 or 2 on narrow windows). */
  effectiveColumns: number;
};

export type HomeSurfaceViewedProps = {
  surface: 'mission_control';
  visitId: string;
  customizeEnabled: boolean;
  tileCount: number;
  isCustomized: boolean;
  columns: DashboardColumns;
  /** `people` when /people redirected here (home as the directory). */
  via?: 'people';
} & HomeViewportProps;

/** One impression per home visit so experiment cohorts have a clean denominator. */
export const logHomeSurfaceViewed = (props: HomeSurfaceViewedProps) => {
  analyticsLogger().logEvent('HOME_SURFACE_VIEWED', props);
};

type HomeTileVisitProps = {
  visitId: string;
  tileId: TileId;
  /** 1-based index in the tile order; 0 for the map banner above the grid. */
  position: number;
  /** On screen before the person scrolled. */
  aboveFold: boolean;
} & HomeViewportProps;

export type HomeTileSeenProps = HomeTileVisitProps & {
  /** Time from opening home until the tile counted as seen. */
  msToSeen: number;
  /** How far the page had been scrolled when the tile was seen. */
  scrollPx: number;
  /** Tile's top edge, measured from the top of the dashboard. */
  tileTopPx: number;
  contentStatus?: TileContentStatus;
};

export type HomeTileDwellProps = HomeTileVisitProps & {
  /** Total time the tile was on screen during the visit. */
  visibleMs: number;
};

export type HomeVisitEndReason = 'navigate' | 'pagehide';

export type HomeVisitSummaryProps = {
  visitId: string;
  endReason: HomeVisitEndReason;
  /** Time home was open in the foreground (hidden tabs and customize mode excluded). */
  durationMs: number;
  maxScrollPx: number;
  /** Furthest share of the dashboard that reached the screen, 0–100. */
  maxSeenPct: number;
  dashboardHeightPx: number;
  tileCount: number;
  seenCount: number;
  tilesRendered: string[];
  tilesSeen: string[];
  tilesAboveFold: string[];
} & HomeViewportProps;

export type HomeAgendaLoadedProps = {
  visitId: string;
  /** Events from the viewer's own linked calendar (next 7 days). */
  personalCount: number;
  /** Events from the shared company calendar (today and tomorrow). */
  companyCount: number;
  /** Timed personal events starting today (viewer's local day). */
  eventsToday: number;
  /** Of `eventsToday`, those with a guest outside the company. */
  externalToday: number;
  /** Minutes until the next timed personal event today; unset when none is left. */
  nextEventInMin?: number;
  calendar: AgendaCalendar;
  /** The agenda tile is on the viewer's board (it can be hidden or not shown yet). */
  tileShown: boolean;
};

/** Once per visit when the agenda first loads: does the viewer see their own calendar? */
export const logHomeAgendaLoaded = (props: HomeAgendaLoadedProps) => {
  analyticsLogger().logEvent('HOME_AGENDA_LOADED', {surface: 'mission_control', ...props});
};

/** A tile was at least half on screen (or filled half the window) for a second. */
export const logHomeTileSeen = (props: HomeTileSeenProps) => {
  analyticsLogger().logEvent('HOME_TILE_SEEN', {surface: 'mission_control', ...props});
};

/** Per seen tile when the visit ends: how long it was on screen in total. */
export const logHomeTileDwell = (props: HomeTileDwellProps) => {
  analyticsLogger().logEvent('HOME_TILE_DWELL', {surface: 'mission_control', ...props});
};

/** One per visit when the person leaves home: scroll depth and which tiles they saw. */
export const logHomeVisitSummary = (props: HomeVisitSummaryProps) => {
  analyticsLogger().logEvent('HOME_VISIT_SUMMARY', {surface: 'mission_control', ...props});
};

/** Amplitude event for Mission Control (homepage dashboard) interactions. */
export const logDashboardInteraction = ({
  action,
  tileId,
  target,
  value,
  eventId,
  destination,
}: DashboardInteractionProps) => {
  analyticsLogger().logEvent('HOME_DASHBOARD_INTERACTION', {
    surface: 'mission_control',
    action,
    tileId,
    target,
    value,
    eventId,
    destination,
  });
};

/** Stock link clicks: legacy HOME_STOCK_CLICKED plus Mission Control tile context. */
export const logStockClick = () => {
  analyticsLogger().logEvent('HOME_STOCK_CLICKED');
  logDashboardInteraction({
    action: 'tile_click',
    tileId: 'stock',
    destination: 'external',
  });
};

/** Strategy map banner clicks. */
export const logStrategyMapBannerClick = () => {
  logDashboardInteraction({
    action: 'tile_click',
    tileId: 'strategyMapBanner',
    destination: 'external',
  });
};

/** Infer link destination type for row/header navigation events. */
export const dashboardLinkDestination = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return 'external';
  }
  if (path.includes('/people/')) {
    return 'profile';
  }
  return 'internal';
};
